/* eslint-disable @typescript-eslint/no-explicit-any */
const tabId = sessionStorage.getItem("sharedStorage/tabId") ?? new Date().toISOString();
sessionStorage.setItem("sharedStorage/tabId", tabId);
/**
 * Creates a function that when called, will be executed on every open tab.
 * `key` is used to identify
 *
 * ```ts
 * const logoff = callOnAllTabs(
 *    "auth.logoff",
 *    () => window.location.assign("/")
 * );
 * logoff();
 * ```
 */
export const callOnAllTabs = (key, fn) => {
    const broadcast = new BroadcastChannel(key);
    broadcast.onmessage = (evt) => fn(...evt.data);
    return (...args) => {
        fn(...args);
        broadcast.postMessage(args);
    };
};
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const respond = callOnAllTabs("sharedStorage.respond", (fromId, key, value) => {
    if (fromId < tabId) {
        sessionStorage.setItem(key, value);
    }
});
const ask = callOnAllTabs("sharedStorage.ask", (key) => {
    const value = sessionStorage.getItem(key);
    if (value)
        respond(tabId, key, value);
});
/**
 * A storage system that keeps `sessionStorage` synced across tabs. Data is cleared once the last tab has been closed.
 *
 * ```
 * sharedStorage.setItem("plugins", JSON.stringify(devPlugins));
 *
 * const plugins = await sharedStorage.fetchItem("plugins");
 * ```
 */
export const sharedStorage = {
    /**
     * Sets the value of the pair identified by key to value for all open tabs.
     */
    setItem: callOnAllTabs("sharedStorage.setItem", (key, value) => {
        sessionStorage.setItem(key, value);
    }),
    /**
     * Removes the key/value pair with the given key from all open tabs.
     */
    removeItem: callOnAllTabs("sharedStorage.removeItem", (key) => {
        sessionStorage.removeItem(key);
    }),
    /**
     * Returns the current value associated with the given key, or null if the given key does not exist.
     *
     * Checks other open tabs for existing value.
     */
    fetchItem: async (key) => {
        ask(key);
        await wait(5);
        return sessionStorage.getItem(key);
    },
};
